import { useMemo, useState } from "react";
import { deskCard, deskEyebrow, deskMuted, deskPrimaryBtn } from "./salesDeskStyles";
import type { SalesDeskOrder } from "./salesDeskOrderFilter";
import { salesDeskRecordDate, salesDeskSameModelSales } from "./salesDeskSaleRecord";

const WINDOWS = [30, 60, 90] as const;

export function SalesDeskMatches({
  sale,
  sales,
  nowMs,
  onOrderAnother,
}: {
  sale: SalesDeskOrder | null;
  sales: SalesDeskOrder[];
  nowMs: number;
  onOrderAnother: (evidenceIds: string[]) => void;
}) {
  const [windowDays, setWindowDays] = useState<(typeof WINDOWS)[number]>(90);
  const matches = useMemo(
    () => (sale ? salesDeskSameModelSales(sales, sale.id, sale.model, windowDays, nowMs) : []),
    [sale, sales, windowDays, nowMs],
  );

  if (!sale) return <p className={deskMuted}>This sale is not on the list.</p>;

  return (
    <div>
      <p className={deskEyebrow}>Matching sales</p>
      <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Does this configuration sell here?</h1>
      <p className={`mt-3 max-w-[40rem] ${deskMuted}`}>
        Other sales with the same model. Style, color, price, and options are not on these records yet.
      </p>
      <section className={`${deskCard} mt-8`}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-[21px] font-semibold">{sale.model.trim() || sale.customer}</h2>
          <label className="text-sm font-semibold">
            Window
            <select
              aria-label="Matching window"
              value={windowDays}
              onChange={(event) => setWindowDays(Number(event.target.value) as (typeof WINDOWS)[number])}
              className="ml-2 rounded-lg border border-[#dce3dc] bg-white px-3 py-2 font-normal dark:border-zinc-700 dark:bg-zinc-900"
            >
              {WINDOWS.map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="mt-4 text-sm">
          <strong>{matches.length}</strong> {matches.length === 1 ? "sale" : "sales"} with this model · {windowDays}-day window
        </p>
        {matches.length === 0 ? <p className={`mt-4 ${deskMuted}`}>No other sale shares this model in this window.</p> : null}
        <div className="mt-2">
          {matches.map((row) => (
            <div key={row.id} className="border-b border-[#dce3dc] py-4 last:border-b-0 dark:border-zinc-700">
              <p className="text-sm font-semibold">
                {row.customer}
                <span className="ml-2 font-normal text-[#687772] dark:text-zinc-400">{row.id.slice(0, 8)}</span>
              </p>
              <p className="mt-1 text-xs text-[#687772] dark:text-zinc-400">
                {salesDeskRecordDate(row.createdAt)} · {row.model}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-full bg-[#eef1e7] px-2.5 py-1 text-[11px] font-semibold text-[#164b43] dark:bg-zinc-800 dark:text-emerald-200">
                  {row.saleStatus}
                </span>
                <span className="rounded-full bg-[#e4edf2] px-2.5 py-1 text-[11px] font-semibold text-[#315d75] dark:bg-zinc-800 dark:text-sky-200">
                  {row.fulfillment}
                </span>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => onOrderAnother(matches.map((row) => row.id))} className={`${deskPrimaryBtn} mt-4`}>
          Order replacement with this evidence
        </button>
      </section>
    </div>
  );
}
